/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react';

import { observer } from 'mobx-react-lite';

import { normalizeDegrees, Speed, UnitConversions } from '../../general-utilities/numbers-util';
import { convertSpeed, SpeedUnit } from '../../general-utilities/units-util';
import { serverBaseWebSocketUrl } from '../../general-utilities/urlUtils';
import Telemetry from '../../models/Telemetry';
import { campaignRootStore } from '../../stores/CampaignRootStore';

const calculateAirSpeed = (telemetryData: Telemetry): number | null => {
  const { weatherData } = campaignRootStore.weatherDataStore;

  if (
    !(
      (telemetryData.ground_speed_m_s === 0 || telemetryData.ground_speed_m_s) &&
      (telemetryData.attitude_euler.yaw_deg === 0 || telemetryData.attitude_euler.yaw_deg) &&
      weatherData
    )
  ) {
    return null;
  }

  const windSpeedKmh = weatherData?.current.wind_speed_10m;
  const windDirection = weatherData?.current.wind_direction_10m;

  const groundSpeed: Speed = {
    value: telemetryData.ground_speed_m_s,
    unit: 'm/s'
  };

  const windSpeed: Speed = {
    value: convertSpeed({
      speed: windSpeedKmh,
      fromUnit: SpeedUnit.KILOMETERS_PER_HOUR,
      toUnit: SpeedUnit.METERS_PER_SECOND
    }),
    unit: 'm/s'
  };

  const heading = normalizeDegrees(telemetryData?.heading_deg);
  const airSpeed = Math.abs(
    UnitConversions.gsToAs({
      groundSpeed,
      windSpeed,
      windDirectionDeg: windDirection,
      droneHeading: heading
    })
  );

  return airSpeed;
};

const updateWeatherDataLocation = async (telemetryData: Telemetry): Promise<void> => {
  await campaignRootStore.weatherDataStore.updateLatLong({
    latitude: telemetryData.position.latitude_deg,
    longitude: telemetryData.position.longitude_deg
  });
};

const processTelemetryData = (telemetryData: Telemetry): void => {
  updateWeatherDataLocation(telemetryData).then(() => {
    const airSpeed = calculateAirSpeed(telemetryData);
    if (airSpeed !== null) {
      campaignRootStore.telemetryStore.updateTelemetryData({
        ...telemetryData,
        airspeed_m_s: airSpeed
      });
    }
  });
};

interface TelemetryControllerProps {
  orgId: number;
  campaignId: string;
  droneUuid: string;
}

export const TelemetryController = observer(({ orgId, campaignId, droneUuid }: TelemetryControllerProps) => {
  const connect = async (): Promise<void> => {
    const { socket } = campaignRootStore.telemetryStore;

    if (socket || !droneUuid) {
      return;
    }

    const wsConnectionStr = `${serverBaseWebSocketUrl}/ws/org/${orgId}/campaign/${campaignId}/drone/${droneUuid}/telemetry`;

    const socketNew = new WebSocket(wsConnectionStr);

    campaignRootStore.telemetryStore.updateSocket(socketNew);

    socketNew.onopen = () => {
      campaignRootStore.telemetryStore.updateIsConnected(true);
      campaignRootStore.weatherDataStore.start();
    };

    socketNew.onmessage = async (event) => {
      const telemetryDataFetched: Telemetry = JSON.parse(event.data);
      processTelemetryData(telemetryDataFetched);
      campaignRootStore.telemetryStore.updateTelemetryData(telemetryDataFetched);
    };

    socketNew.onclose = () => {
      campaignRootStore.telemetryStore.updateIsConnected(false);
      campaignRootStore.telemetryStore.updateSocket(null);
      campaignRootStore.weatherDataStore.stop();
    };

    socketNew.onerror = () => {
      socketNew.close();
    };
  };

  const disconnect = (): void => {
    const { socket } = campaignRootStore.telemetryStore;

    if (socket) {
      socket.close();
      campaignRootStore.telemetryStore.updateIsConnected(false);
      campaignRootStore.telemetryStore.updateSocket(null);
      campaignRootStore.weatherDataStore.stop();
    }
  };

  useEffect(() => {
    connect();
    return () => disconnect();
  }, []);

  useEffect(() => {
    disconnect();
    connect();
  }, [droneUuid]);

  return null;
});
